import { z } from "zod";
import {
  lenientInternationalPhoneNumberSchema,
  nameSchema,
  panSchema,
  phoneNumberSchema,
} from "./common";

export const ClientStates = [
  "LEAD",
  "VERIFICATION_PENDING",
  "SETUP_PENDING",
  "UNDER_REVIEW",
  "ACTIVE",
  "INACTIVE",
  "UPDATE_PENDING",
  "UPDATE_UNDER_REVIEW",
  "AUTHENTICATION_PENDING",
] as const;

export const ClientStatusSchema = z.enum(ClientStates);

export const KYCStatusSchema = z.enum(["S", "F", "U"]);

// Whether a client account is held singly or jointly (anyone or survivor)
export const ClientHoldingPatternSchema = z.enum([
  "single",
  "anyone_or_survivor",
  "joint",
]);

export const KRANameSchema = z.enum([
  "camskra",
  "cvlkra",
  "ndmlkra",
  "karvykra",
]);

export const ClientSchema = z.object({
  id: z.number(),
  pan: z.string(),
  phone_number: z.string(),
  email: z.string().email().nullable(),
  name: z.string(),
  first_name: z.string().optional(),
  status: ClientStatusSchema,
  created_at: z.string(),
  modified_at: z.string(),
  created_by: z.number().nullable().optional(),
  last_seen: z.string().nullable(),
  investment_account: z.string().nullable().optional(),
  primary_profile_id: z.string(),
  remark: z.string().nullable().optional(),
  kyc_status: KYCStatusSchema,
  kyc_status_remark: z.string().nullable(),
  kyc_kra_name: KRANameSchema.nullable(),
  kyc_status_date: z.string().nullable(),
  kyc_application_remark: z.string().nullable(),
  kyc_application_status_code: z.string().nullable(),
  kyc_reason: z.string().nullable().optional(),
  aum: z.string().nullable().optional(),
  internal_aum: z.string().nullable().optional(),
  external_aum: z.string().nullable().optional(),
  overall_aum: z.string().nullable().optional(),
  tax_status: z.string().optional(),
  tax_status_display: z.string().optional(),
  holding_pattern: ClientHoldingPatternSchema.optional(),
  employee_id: z.number().nullable().optional(),
  family_name: z.string().nullable().optional(),
  family_aum: z.string().nullable().optional(),
  family_id: z.number().nullable().optional(),
  family_feature_viewed: z.boolean().optional(),
  hyperverge_kyc_application_status: z.string().nullable().optional(),
  total_sip_book: z.string().nullable().optional(),
  risk_profile: z.string().nullable(),
});

export type Client = z.infer<typeof ClientSchema>;

export const CreateClientSchema = z
  .object({
    pan: z.literal("").or(panSchema),
    phone_number: lenientInternationalPhoneNumberSchema,
    name: nameSchema,
    holding_pattern: ClientHoldingPatternSchema.optional(),
    tax_status: z.string().optional(),
    request_consent: z.boolean().optional(),
    email: z
      .union([z.string().email(), z.literal("")])
      .nullable()
      .optional(),
  })
  .superRefine((data, ctx) => {
    // Only NRI clients can have a non-Indian mobile number
    if (data.tax_status !== "nri" && !data.phone_number.startsWith("+91")) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Only NRI clients can use a non-Indian mobile number",
        path: ["phone_number"],
      });
    }
    if (data.request_consent && !data.pan) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "PAN is required for requesting investor consent",
        path: ["pan"],
      });
    }
  });

export const UpdateClientSchema = z.object({
  pan: z.literal("").or(panSchema),
  phone_number: lenientInternationalPhoneNumberSchema,
  name: nameSchema,
  holding_pattern: ClientHoldingPatternSchema.optional(),
  email: z
    .union([z.string().email(), z.literal("")])
    .nullable()
    .optional(),
});

export const RequestConsentClientSchema = z.object({
  pan: panSchema,
  phone_number: phoneNumberSchema,
  name: nameSchema,
  email: z
    .union([z.string().email(), z.literal("")])
    .nullable()
    .optional(),
});
